import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useIntl } from 'react-intl';

import CardSelectionController from 'src/components/Dashboard/CardSelectionController';
import {
  buildChartViewKey,
  getCardSelectionState,
  getChartSummaryData,
  selectedKeysToggleKey,
} from 'src/components/Dashboard/helpers/common';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import { APIConfig } from 'src/config';
import Chart from 'src/enosikit/components/Chart';
import Cards from 'src/enosikit/components/Chart/components/Cards';
import { meterContent, tradeContent } from 'src/enosikit/components/Chart/helpers/cards';
import { getTotalCarbonEmission } from 'src/lib/carbondata/helpers';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
  DEFAULT_MAX_VISIBLE_CARDS_COUNT,
  SOURCE, SOURCE_HISTORIAN, SOURCE_TRADES,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/util/constants';

import { meterChartTooltip, tradeChartTooltip } from './helpers/chartTooltip';
import {
  buildCardsData, buildChartData, meterViewCardLabel,
  tradeViewCardLabel,
} from './helpers/propertyShowChart';

/**
 * PropertyShowChart component displays charts and cards for property data visualization.
 * Supports both meter view (historian data) and trade view with lazy loading of cards.
 * @param {object} props - Component props.
 * @param {object} props.chartView - Chart view configuration.
 * @param {string} props.chartView.groupBy - How to group the data (counterparty or trade type).
 * @param {string} props.chartView.aggregateBy - How to aggregate the data (property or meter).
 * @param {Function} props.controlSetStateFunc - Function to update control state.
 * @param {Function} props.handleTimespanUpdate - Function to handle
 * timespan updates on chart click.
 * @param {string} props.historianAggregation - Aggregation level for historian data.
 * @param {object} props.mainData - Main data object containing chart and card data.
 * @param {string} [props.noDataMessage] - Message to display when the user cancelled
 * a request and there is no current data available.
 * @param {Function} [props.onChartReadyFunc] - Callback function invoked when the chart
 * has finished its initial render and is ready for interaction.
 * @param {string} props.source - Data source (historian or trades).
 * @param {object} props.timespan - Timespan object with start and finish dates.
 * @param {string} props.timezone - Timezone for date display.
 * @param {string} props.tradeAggregation - Aggregation level for trade data.
 * @param {string} props.unit - Unit of measurement (energy, carbon, or currency).
 * @returns {React.ReactElement} The rendered PropertyShowChart component.
 */
function PropertyShowChart(props) {
  const {
    controlSetStateFunc, chartView, historianAggregation, mainData, noDataMessage,
    onChartReadyFunc, source, timespan, timezone, tradeAggregation, unit,
    handleTimespanUpdate,
  } = props;

  const intl = useIntl();
  const chartRef = useRef(null);

  const [hoverKeys, setHoverKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [tooltipTimestamp, setTooltipTimestamp] = useState(null);
  const tooltipTimestampRef = useRef(tooltipTimestamp);
  const isLoadingMoreRef = useRef(false);
  const [chartSize, setChartSize] = useState({ height: 0, width: 0 });
  const [meterViewVisibleCards, setMeterViewVisibleCards] = useState(
    DEFAULT_MAX_VISIBLE_CARDS_COUNT,
  );
  const [tradeViewVisibleCards, setTradeViewVisibleCards] = useState(
    DEFAULT_MAX_VISIBLE_CARDS_COUNT,
  );
  const hasCalledOnChartReadyRef = useRef(false);

  const setChartBounds = useCallback(() => {
    if (!chartRef.current) return;

    // Note this excludes padding so ...
    let { clientHeight: height, clientWidth: width } = chartRef.current;
    // ...need to get the computed padding.
    const {
      paddingTop, paddingBottom, paddingLeft, paddingRight,
    } = window.getComputedStyle(chartRef.current);

    if (paddingTop !== '') { height -= parseFloat(paddingTop); }
    if (paddingBottom !== '') { height -= parseFloat(paddingBottom); }
    if (paddingLeft !== '') { width -= parseFloat(paddingLeft); }
    if (paddingRight !== '') { width -= parseFloat(paddingRight); }

    setChartSize({ height, width });
  }, []);

  useEffect(() => {
    // Victory sets the SVG width attribute from the `width` prop. When the
    // actual container width changes (sidebar toggles, CSS transitions, layout
    // shifts) but the prop stays stale, the browser scales the entire SVG to
    // fit — making axis labels and tooltip text appear larger or smaller even
    // though their font-size attribute is unchanged.
    //
    // The previous window 'resize' listener only fired on browser-window
    // resizes, missing container-only changes. ResizeObserver fires whenever
    // the observed element's dimensions change for any reason, keeping the
    // width prop in sync and preventing the SVG scaling mismatch.
    // Falls back to window 'resize' when ResizeObserver is unavailable.
    const node = chartRef.current;
    if (!node) return undefined;

    // Measure immediately on mount so the first render has accurate dimensions.
    setChartBounds();

    if (typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', setChartBounds);

      return () => {
        window.removeEventListener('resize', setChartBounds);
      };
    }

    const resizeObserver = new window.ResizeObserver(() => {
      setChartBounds();
    });
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }, [setChartBounds]);

  useEffect(() => {
    if (Object.keys(selectedKeys).length > 0) {
      setSelectedKeys({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const cardsHoverFunc = useCallback((key, hover) => {
    // Skip hover updates during "Load More" click to prevent unintentional re-renders
    if (isLoadingMoreRef.current) {
      return;
    }
    setHoverKeys((prevHoverKeys) => {
      const hasKey = prevHoverKeys.includes(key);
      if (hover && !hasKey) {
        // Add key - need new array
        return [...prevHoverKeys, key];
      }
      if (!hover && hasKey) {
        // Remove key - need new array
        return prevHoverKeys.filter((k) => k !== key);
      }
      // No change - return same reference
      return prevHoverKeys;
    });
  }, []);

  const cardsSelectedFunc = useCallback((key) => {
    const chartViewKey = buildChartViewKey(chartView, source);
    setSelectedKeys((prevSelectedKeys) => {
      const updatedKeys = selectedKeysToggleKey(prevSelectedKeys, chartViewKey, key);
      return updatedKeys;
    });
    setHoverKeys((prev) => (prev.length > 0 ? [] : prev));
  }, [chartView, source]);

  const loadMoreMeterViewCards = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isLoadingMoreRef.current = true;
    setMeterViewVisibleCards((prev) => prev + DEFAULT_MAX_VISIBLE_CARDS_COUNT);
    // Reset after a short delay to allow normal hover behavior again
    requestAnimationFrame(() => {
      isLoadingMoreRef.current = false;
    });
  }, []);

  const loadMoreTradeViewCards = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isLoadingMoreRef.current = true;
    setTradeViewVisibleCards((prev) => prev + DEFAULT_MAX_VISIBLE_CARDS_COUNT);
    // Reset after a short delay to allow normal hover behavior again
    requestAnimationFrame(() => {
      isLoadingMoreRef.current = false;
    });
  }, []);

  const tradesLabel = intl.formatMessage({ id: 'property.property_show.chart.control.source.trades.title', defaultMessage: 'Trades' });
  const meterLabel = intl.formatMessage({ id: 'property.property_show.chart.control.source.historian.title', defaultMessage: 'Meter' });

  const controlOptionFunc = () => {
    const typeOpts = {
      group: SOURCE,
      items: [{
        value: SOURCE_TRADES,
        label: tradesLabel,
        active: (source === SOURCE_TRADES),
        disabled: false,
      }, {
        value: SOURCE_HISTORIAN,
        label: meterLabel,
        active: (source === SOURCE_HISTORIAN),
        disabled: false,
      }],
    };
    return [typeOpts];
  };

  const tooltipUpdate = useCallback((timestamp) => {
    setTooltipTimestamp(timestamp);
    tooltipTimestampRef.current = timestamp;
  }, []);

  // Use ref to avoid recreating handleChartClick when tooltipTimestamp changes
  const handleChartClick = useCallback(() => {
    if (historianAggregation.match(/^\d+S$/)) {
      return;
    }
    if (tooltipTimestampRef.current === null) {
      return;
    }

    handleTimespanUpdate(tooltipTimestampRef.current, tooltipTimestampRef.current);
  }, [historianAggregation, handleTimespanUpdate]);

  if (!props) {
    return <Loading />;
  }

  const { width } = chartSize;
  const { aggregateBy } = chartView || {};
  const isByMeter = aggregateBy === DATA_AGGREGATE_BY_METER;
  const metersLength = Object.values(mainData?.meters)?.length || 0;
  const multipleMeter = metersLength > 1;

  const meterView = source === SOURCE_HISTORIAN;
  const tradeView = source === SOURCE_TRADES;

  /**
   * Generates the content for a meter card.
   * @param {object} cardDatum - An object containing the data for the meter card.
   * @param {string} cardDatum.title - The title of the meter.
   * @param {string} cardDatum.identifier - The identifier of the meter.
   * @param {object} cardDatum.buy - The buy object for the meter card.
   * @param {number} cardDatum.buy.volume - The buy volume for the meter card.
   * @param {object} cardDatum.sell - The sell object for the meter card.
   * @param {number} cardDatum.sell.volume - The sell volume for the meter card.
   * @returns {React.ReactElement} - The rendered meter card content.
   */
  const meterViewCard = useCallback((cardDatum) => {
    if (!isByMeter && !meterView) {
      return null;
    }

    const {
      title,
      identifier,
      buy,
      sell,
    } = cardDatum;

    const { volume: buyVolume } = buy || {};
    const { volume: sellVolume } = sell || {};

    const cardLabel = meterViewCardLabel(title, identifier);

    return meterContent(intl, cardLabel, buyVolume, sellVolume);
  }, [intl, isByMeter, meterView]);

  /**
   * Generates the content for a trade card.
   * @param {object} cardDatum - An object containing the data for the trade card.
   * @param {string} cardDatum.title - The property or meter's title.
   * @param {string} cardDatum.identifier - The meter identifier.
   * @param {object} cardDatum.property - The property object.
   * @param {string} cardDatum.label - The trade type or property address.
   * @param {string} cardDatum.subLabel - The counterparty name.
   * @returns {React.ReactElement} - The content for the trade card.
   */
  const tradeViewCard = useCallback((cardDatum) => {
    const { label } = cardDatum;

    const cardsLabel = tradeViewCardLabel(intl, cardDatum, chartView);

    return tradeContent(intl, label, cardDatum, cardsLabel);
  }, [intl, chartView]);

  const { hasCounterfactual } = mainData;

  // Memoize expensive derived data to prevent recalculation on every render
  const chartData = useMemo(
    () => buildChartData(intl, meterView, mainData, timezone, chartView),
    [intl, meterView, mainData, timezone, chartView],
  );

  const chartSummaryData = useMemo(
    () => getChartSummaryData(mainData),
    [mainData],
  );

  const cardsData = useMemo(
    () => buildCardsData(meterView, chartData, chartView),
    [meterView, chartData, chartView],
  );

  const totalCarbonEmission = useMemo(
    () => getTotalCarbonEmission(mainData?.buy?.data),
    [mainData],
  );

  // Reset the onChartReadyFunc flag when mainData changes (new data load)
  const mainDataRef = useRef(mainData);
  useEffect(() => {
    if (mainDataRef.current !== mainData) {
      hasCalledOnChartReadyRef.current = false;
      mainDataRef.current = mainData;
    }
  }, [mainData]);

  // Notify parent that chart is ready after first render with computed data
  useEffect(() => {
    if (chartData && !hasCalledOnChartReadyRef.current) {
      // Use double requestAnimationFrame to ensure paint has completed.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (onChartReadyFunc) {
            onChartReadyFunc();
          }
          hasCalledOnChartReadyRef.current = true;
        });
      });
    }
    return undefined;
  }, [chartData, onChartReadyFunc]);

  const {
    ChartControls, ChartSummary, ChartMeter, ChartTrade,
  } = Chart;

  // Memoize the visible cards data to prevent recalculation on every render
  const visibleMeterViewCards = useMemo(
    () => cardsData.slice(0, meterViewVisibleCards),
    [cardsData, meterViewVisibleCards],
  );

  const visibleTradeViewCards = useMemo(
    () => cardsData.slice(0, tradeViewVisibleCards),
    [cardsData, tradeViewVisibleCards],
  );

  const chartViewKey = buildChartViewKey(chartView, source);
  const {
    finalSelectedKeys,
    hasCurrentViewSelectedKeys,
    hasAnySelectedKeys,
    enableClearBtn,
    cardsDataAvailable,
  } = useMemo(
    () => getCardSelectionState(selectedKeys, chartViewKey, cardsData),
    [selectedKeys, chartViewKey, cardsData],
  );

  const handleClearCurrentSelection = useCallback(() => {
    setSelectedKeys((prevSelectedKeys) => ({
      ...prevSelectedKeys,
      [chartViewKey]: [],
    }));
  }, [chartViewKey]);

  const handleClearAllSelections = useCallback(() => {
    setSelectedKeys({});
  }, []);

  return (
    <>
      <div className="mt-4 mb-4 card">
        <div className="card-body" ref={chartRef}>
          <div className="d-flex justify-content-between">
            <div className="flex-fill justify-content-center order-2 order-xs-3 col-xs-12">
              <ChartControls
                buttons={controlOptionFunc()}
                onButtonClick={(opts) => { controlSetStateFunc(opts); }}
              />
            </div>
            <div className="flex-fill order-1 order-xs-1 col-xs-12">
              <ChartSummary
                align="left"
                category="imports"
                hasCounterfactual={hasCounterfactual}
                counterfactual={chartSummaryData.buy.netDiffValue}
                tradedEnergy={chartSummaryData.buy.tradedEnergy}
                tradedValue={chartSummaryData.buy.tradedValue}
                untradedEnergy={chartSummaryData.buy.untradedVolume}
                untradedValue={chartSummaryData.buy.untradedValue}
                rebatedEnergy={chartSummaryData.buy.rebatedEnergy}
                carbonData={totalCarbonEmission}
                mode={APIConfig().MODE}
              />
            </div>
            <div className="flex-fill order-3 order-xs-2 col-xs-12">
              <ChartSummary
                align="right"
                category="exports"
                hasCounterfactual={hasCounterfactual}
                counterfactual={chartSummaryData.sell.netDiffValue}
                tradedEnergy={chartSummaryData.sell.tradedEnergy}
                tradedValue={chartSummaryData.sell.tradedValue}
                untradedEnergy={chartSummaryData.sell.untradedVolume}
                untradedValue={chartSummaryData.sell.untradedValue}
                rebatedEnergy={chartSummaryData.sell.rebatedEnergy}
                mode={APIConfig().MODE}
              />
            </div>
          </div>
          {noDataMessage && (
            <div className="d-flex justify-content-center align-items-center">
              <div className="alert alert-warning">
                {noDataMessage}
              </div>
            </div>
          )}
          {meterView && !noDataMessage && (
            <ChartMeter
              aggregation={historianAggregation}
              chartView={chartView}
              data={chartData}
              handleChartClick={handleChartClick}
              hoverKeys={hoverKeys}
              isByMeter={isByMeter}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={meterChartTooltip}
              tooltipTimestamp={tooltipTimestamp}
              tooltipUpdate={tooltipUpdate}
              unit={unit}
              width={width}
            />
          )}
          {tradeView && !noDataMessage && (
            <ChartTrade
              aggregation={tradeAggregation}
              chartView={chartView}
              handleChartClick={handleChartClick}
              hoverKeys={hoverKeys}
              multipleMeter={multipleMeter}
              selectedKeys={finalSelectedKeys}
              timespan={timespan}
              timezone={timezone}
              tooltipLabelFunc={tradeChartTooltip}
              tooltipTimestamp={tooltipTimestamp}
              tooltipUpdate={tooltipUpdate}
              tradeDataBuy={chartData.buy}
              tradeDataSell={chartData.sell}
              unit={unit}
              width={width}
            />
          )}
        </div>
      </div>
      {cardsDataAvailable && (
        <div className="d-flex justify-content-end mb-2">
          <CardSelectionController
            enableClearBtn={enableClearBtn}
            hasCurrentViewSelectedKeys={hasCurrentViewSelectedKeys}
            hasAnySelectedKeys={hasAnySelectedKeys}
            onClearCurrentSelectionFunc={handleClearCurrentSelection}
            onClearAllSelectionsFunc={handleClearAllSelections}
          />
        </div>
      )}
      {(meterView && isByMeter && cardsData.length > 0) && (
        <div className="position-relative">
          <Cards
            cardFunc={meterViewCard}
            data={visibleMeterViewCards}
            hoverFunc={cardsHoverFunc}
            hoverKeys={hoverKeys}
            selectedFunc={cardsSelectedFunc}
            selectedKeys={finalSelectedKeys}
          />
          {cardsData.length > DEFAULT_MAX_VISIBLE_CARDS_COUNT && (
            <div className="d-flex justify-content-end mb-4">
              <LoadMoreButton
                copyLoadMore={intl.formatMessage({ id: 'property.property_show.chart.cards.load_more.label', defaultMessage: 'Load more...' })}
                copyNoMore={intl.formatMessage({ id: 'property.property_show.chart.cards.no_more.label', defaultMessage: 'No more cards to load' })}
                hasNext={cardsData.length > meterViewVisibleCards}
                onClick={loadMoreMeterViewCards}
              />
            </div>
          )}
        </div>
      )}
      {tradeView && cardsData.length > 0 && (
        <div className="position-relative">
          <Cards
            cardFunc={tradeViewCard}
            data={visibleTradeViewCards}
            hoverFunc={cardsHoverFunc}
            hoverKeys={hoverKeys}
            selectedFunc={cardsSelectedFunc}
            selectedKeys={finalSelectedKeys}
          />
          {cardsData.length > DEFAULT_MAX_VISIBLE_CARDS_COUNT && (
            <div className="d-flex justify-content-end mb-4">
              <LoadMoreButton
                copyLoadMore={intl.formatMessage({ id: 'property.property_show.chart.cards.load_more.label', defaultMessage: 'Load more...' })}
                copyNoMore={intl.formatMessage({ id: 'property.property_show.chart.cards.no_more.label', defaultMessage: 'No more cards to load' })}
                hasNext={cardsData.length > tradeViewVisibleCards}
                onClick={loadMoreTradeViewCards}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

PropertyShowChart.propTypes = {
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_PROPERTY,
      DATA_AGGREGATE_BY_METER,
    ]).isRequired,
  }).isRequired,
  controlSetStateFunc: PropTypes.func.isRequired,
  handleTimespanUpdate: PropTypes.func.isRequired,
  historianAggregation: PropTypes.string.isRequired,
  mainData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  noDataMessage: PropTypes.string,
  onChartReadyFunc: PropTypes.func,
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  tradeAggregation: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  unit: PropTypes.oneOf([UNIT_ENERGY, UNIT_CARBON, UNIT_CURRENCY]).isRequired,
  source: PropTypes.oneOf([SOURCE_HISTORIAN, SOURCE_TRADES]).isRequired,
};

PropertyShowChart.defaultProps = {
  noDataMessage: null,
  onChartReadyFunc: null,
};

export default React.memo(PropertyShowChart);
